import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles, fade } from '@material-ui/core/styles';
import { Chip, Tooltip } from '@material-ui/core';
import {
  Cancel as CancelIcon,
  ReportProblemOutlined as MissingIcon,
  VisibilityOffOutlined as HiddenIcon,
} from '@material-ui/icons';

export const REPARTO_NON_PRESENTE = "Reparto non più presente nell'esercizio";
export const REPARTO_FUORI_CARTELLA = 'Non vede la cartella di destinazione';
export const REPARTO_EREDITATO = 'Visibilità ereditata dalla cartella';

const useStyles = makeStyles((theme) => ({
  // The same tint the section uses for its active states, with the brand's
  // darker shade on top: a reparto is a fact about the esercizio, not an
  // alert, so it takes the brand and not a semantic colour.
  reparto: {
    backgroundColor: fade(theme.palette.primary.main, 0.12),
    color: theme.palette.primary.dark,
    fontWeight: 500,
  },
  // Outlined in the same brand shade: the reparto is a fact about the folder
  // above, not about this row, so it is drawn and not filled.
  ereditato: {
    borderColor: fade(theme.palette.primary.main, 0.5),
    color: theme.palette.primary.dark,
    fontWeight: 500,
  },
  // Dashed and grey: the restriction is still there, the reparto behind it is
  // not, or does not see the folder. The icon says the same thing to whoever
  // cannot tell a dashed border from a solid one, and the tooltip spells out
  // which of the two it is.
  avviso: {
    borderStyle: 'dashed',
    color: theme.palette.text.secondary,
  },
  avvisoIcona: {
    color: theme.palette.text.secondary,
  },
}));

const AVVISI = {
  nonPresente: { Icona: MissingIcon, testo: REPARTO_NON_PRESENTE },
  fuoriCartella: { Icona: HiddenIcon, testo: REPARTO_FUORI_CARTELLA },
};

/**
 * One reparto of a visibility restriction, as a chip.
 *
 * `avviso` says what is wrong with it, when something is: `nonPresente` for a
 * reparto whose name the esercizio no longer has — renamed or removed since the
 * restriction was saved — which the server still honours and the panel must not
 * drop in silence; `fuoriCartella` for a reparto that does not see the folder
 * the element is going into, so that the element would be visible to nobody.
 *
 * `ereditata` marks a reparto the row gets from its folder rather than from a
 * restriction of its own. With `onDelete` the chip can be taken out of a
 * selection, which is the only way out for a missing reparto, since no option
 * in the menu matches it any more.
 */
export default function RepartoChip({
  nome,
  avviso,
  ereditata,
  onDelete,
  size,
}) {
  const classes = useStyles();
  const dettaglio = avviso ? AVVISI[avviso] : null;

  let className = classes.reparto;
  if (dettaglio) className = classes.avviso;
  else if (ereditata) className = classes.ereditato;

  const chip = (
    <Chip
      size={size}
      label={nome}
      variant={dettaglio || ereditata ? 'outlined' : 'default'}
      className={className}
      icon={
        dettaglio ? (
          <dettaglio.Icona className={classes.avvisoIcona} />
        ) : undefined
      }
      onDelete={onDelete}
      deleteIcon={
        onDelete ? (
          <CancelIcon
            titleAccess={`Rimuovi ${nome}`}
            // Inside a Select the chips sit on the element that opens the
            // menu on mousedown; removing a reparto must not also open it.
            onMouseDown={(event) => event.stopPropagation()}
          />
        ) : undefined
      }
    />
  );

  if (dettaglio) return <Tooltip title={dettaglio.testo}>{chip}</Tooltip>;
  if (ereditata) return <Tooltip title={REPARTO_EREDITATO}>{chip}</Tooltip>;
  return chip;
}

RepartoChip.propTypes = {
  nome: PropTypes.string.isRequired,
  avviso: PropTypes.oneOf(['nonPresente', 'fuoriCartella']),
  ereditata: PropTypes.bool,
  onDelete: PropTypes.func,
  size: PropTypes.oneOf(['small', 'medium']),
};

RepartoChip.defaultProps = {
  avviso: null,
  ereditata: false,
  size: 'small',
};
